import React, { useEffect, useState, useMemo } from "react";
import { Row, Col } from "antd";
import {
  FileTextOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import useWindowSize from "utilFunctions/CustomHooks/useWindowSize";
import styled from "styled-components";

// Import sub-components
import StatsCards from "./Components/StatsCards";
import SubjectSummaryCards from "./Components/SubjectSummaryCards";
import SubmissionProgress from "./Components/SubmissionProgress";
import TestConfiguration from "./Components/TestConfiguration";
import ImportantDates from "./Components/ImportantDates";
import EvaluateTab from "./Components/EvaluateTab";
import { StyledTabs } from "Pages/MyBatchesAntd/MyBatchesAntd";
import TestEvaluationHeader from "Pages/TestEvaluation/Components/TestEvaluationHeader";
import { useLocation, useNavigate } from "react-router-dom";
import MyBatchSyllabusTab from "Pages/MyBatches/MyBatch/MyBatchTabs/MyBatchSyllabusTab";
import { pushToDataLayerForGTM } from "utilFunctions/helperFunctions/analytics";
import {
  useSubjectiveTest,
  useSubjectiveTestSummary,
} from "Pages/DRS/Context/MyBatchContext";

export const MainContainer = styled.div<{ screen: string }>`
  padding: ${(props) =>
    ["xs", "sm", "md"].includes(props.screen) ? "0px" : "24px 24px 0px 43px"};
  background-color: #f8fbff;
  min-height: 100vh;
`;

interface TestInfo {
  id?: number;
  format?: string;
  mode?: string;
  startDate?: string;
  endDate?: string;
  evaluationDate?: string;
  resultDate?: string;
  testName?: string;
}

const TestEvaluation = () => {
  const [testInfo, setTestInfo] = useState<any>({});
  const { state } = useLocation();
  const {
    batchId,
    batchDetail,
    test,
    action,
    batchDetailsData,
    batchDetailById,
  } =
    (state as {
      batchId: string;
      batchDetail: any;
      test: any;
      batchDetailsData: any;
      action?: string;
      batchDetailById?: any;
    }) || {};
  const navigate = useNavigate();
  const { screen } = useWindowSize();
  const [activeTab, setActiveTab] = useState("3");
  const { fetchSubjectiveTest, subjectiveTestData } = useSubjectiveTest();
  const { fetchSubjectiveTestSummary, subjectiveTestSummaryData = {} } =
    useSubjectiveTestSummary();
  const Test_Configuration = (subjectiveTestData as any)?.data || {};
  const [selectedCourseSyllabus, setSelectedCourseSyllabus] = useState<any>(
    batchDetailById?.[0]
  );
  useEffect(() => {
    const { test, tabIndex } =
      (state as { test: TestInfo; tabIndex?: string }) || {};
    if (tabIndex) {
      setActiveTab(tabIndex);
    }
    if (!test?.id) return;
    setTestInfo(test);
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
    document.body.classList.add("noOverflow");
    return () => {
      document.body.classList.remove("noOverflow");
    };
  }, [state]);
  useEffect(() => {
    if (batchId && testInfo?.id) {
      fetchSubjectiveTest(batchId, testInfo.id, testInfo.testId);
      fetchSubjectiveTestSummary(batchId, testInfo.id);
    }
  }, [batchId, testInfo?.id]);
  const summary = (subjectiveTestSummaryData as any)?.data || {};
  const totalStudents = summary?.totalStudents;
  const subjectSummary = summary?.subjectSummary || {};

  const stats = useMemo(
    () => [
      {
        label: "Submissions",
        value: summary?.submissions?.toString() || "0",
        total: totalStudents?.toString() || "0",
        icon: <FileTextOutlined />,
        color: "#e6f7ff", // Light blue bg
        iconColor: "#1890ff",
      },
      {
        label: "Evaluated",
        value: summary?.evaluated?.toString() || "0",
        total: (summary?.submissions || 0).toString(),
        icon: <CheckCircleOutlined />,
        color: "#f6ffed", // Light green bg
        iconColor: "#52c41a",
      },
      {
        label: "Pending",
        value: summary?.pending?.toString() || "0",
        total: (summary?.submissions || 0).toString(),
        icon: <ClockCircleOutlined />,
        color: "#fff7e6", // Light orange bg
        iconColor: "#faad14",
      },
      {
        label: "Challenged",
        value: summary?.challenged?.toString() || "0",
        total: (summary?.submissions || 0).toString(),
        icon: <WarningOutlined />,
        color: "#fff1f0", // Light red bg
        iconColor: "#ff4d4f",
      },
    ],
    [
      summary?.submissions,
      summary?.evaluated,
      summary?.pending,
      summary?.challenged,
      totalStudents,
    ]
  );

  const progressData = useMemo(
    () => ({
      submissionRate:
        summary.submissions && totalStudents
          ? parseFloat(((summary.submissions / totalStudents) * 100).toFixed(1))
          : 0,

      evaluationProgress:
        summary.evaluated && summary.submissions
          ? parseFloat(
              ((summary.evaluated / summary.submissions) * 100).toFixed(1)
            )
          : 0,
      submittedCount: summary.submissions || 0,
      totalStudents: totalStudents,

      gradedCount: summary.evaluated || 0,
    }),
    [summary.submissions, summary.evaluated, totalStudents]
  );
  const renderOverview = useMemo(
    () => (
      <div style={{ marginTop: 12 }}>
        <Row gutter={[24, 24]}>
          <Col xs={24} lg={14}>
            <TestConfiguration
              subject={Test_Configuration?.subject}
              duration={Test_Configuration?.duration}
              format={testInfo?.format}
              mode={testInfo?.mode}
              totalMarks={Test_Configuration?.totalMarks}
              testId={testInfo?.testId}
              externalTestScheduleId={testInfo?.externalScheduleId}
            />
          </Col>
          <Col xs={24} lg={10}>
            <SubmissionProgress
              submissionRate={progressData.submissionRate}
              evaluationProgress={progressData.evaluationProgress}
              submittedCount={progressData.submittedCount}
              totalStudents={progressData.totalStudents}
              gradedCount={progressData.gradedCount}
            />
            <ImportantDates
              resultDate={test?.testResultDate}
              testInfo={testInfo}
            />
          </Col>
        </Row>
      </div>
    ),
    [
      Test_Configuration?.subject,
      Test_Configuration?.duration,
      Test_Configuration?.totalMarks,
      testInfo?.format,
      testInfo?.mode,
      testInfo?.startDate,
      testInfo?.endDate,
      testInfo?.evaluationDate,
      testInfo?.resultDate,
      progressData,
    ]
  );

  const hasSubjectSummary = true;
  // Object.keys(subjectSummary).length > 0 &&
  // Object.values(subjectSummary).some(
  //   (arr) => Array.isArray(arr) && arr.length > 0
  // );

  const statsComponent = useMemo(
    () => (
      <>
        {hasSubjectSummary ? (
          <SubjectSummaryCards
            subjectSummary={subjectSummary}
            totalSubmissions={summary?.submissions || 0}
            totalStudents={totalStudents}
            summary={summary}
          />
        ) : (
          <StatsCards
            stats={stats.map((s) => ({
              ...s,
              bgColor: s.color, // Using the light color defined in stats array
              color: s.iconColor, // Using the primary color for icons
            }))}
            screen={screen}
          />
        )}
      </>
    ),
    [
      stats,
      screen,
      hasSubjectSummary,
      subjectSummary,
      summary?.submissions,
      totalStudents,
    ]
  );
  console.log("courseId and subj in TestEvaluation:", batchDetailById);
  const items = useMemo(
    () => [
      {
        key: "1",
        label: <span className="d-flex gap-2">Overview</span>,
        children: (
          <>
            {statsComponent}
            {renderOverview}
          </>
        ),
      },
      {
        key: "2",
        label: <span className="d-flex gap-2">Syllabus</span>,
        children: (
          <>
            {statsComponent}
            <div style={{ marginTop: 12, textAlign: "center" }}>
              <MyBatchSyllabusTab
                screen={screen}
                selectedCourseSyllabus={selectedCourseSyllabus}
                setSelectedCourseSyllabus={setSelectedCourseSyllabus}
                argBatchId={batchId}
                item={batchDetailById}
              ></MyBatchSyllabusTab>
            </div>
          </>
        ),
      },
      {
        key: "3",
        label: <span className="d-flex gap-2">Evaluate</span>,
        children: (
          <>
            {statsComponent}
            <EvaluateTab
              totalMarks={Test_Configuration?.totalMarks}
              action={action}
              type="evaluate"
            />
          </>
        ),
      },
      {
        key: "4",
        label: <span className="d-flex gap-2">Results</span>,
        children: (
          <>
            {statsComponent}
            <EvaluateTab
              totalMarks={Test_Configuration?.totalMarks}
              action={action}
              type="results"
            />
          </>
        ),
      },
    ],
    [
      statsComponent,
      renderOverview,
      screen,
      selectedCourseSyllabus,
      batchDetailById,
      batchId,
      Test_Configuration?.totalMarks,
      action,
    ]
  );
  const onBackClick = () => {
    const { batchId } = state || {};
    navigate("/admin/mybatch/" + batchId, {
      replace: false,
      state,
    });
  };
  return (
    <MainContainer screen={screen}>
      <TestEvaluationHeader
        activeTab={activeTab}
        testInfo={testInfo}
        batchDetail={batchDetail}
        batchDetailsData={batchDetailsData}
        onBackClick={onBackClick}
        batchDetailById={batchDetailById}
        selectedCourseSyllabus={selectedCourseSyllabus}
        selectedCourseSyllabusHandler={setSelectedCourseSyllabus}
      ></TestEvaluationHeader>
      <StyledTabs
        activeKey={activeTab}
        onChange={(key: string) => {
          setActiveTab(key);
          pushToDataLayerForGTM({
            event: "ag_test_compliance_test_detail_tab_switched",
            TEST_ID: testInfo?.id,
            STATUS_TAB: (state as any)?.statusTab,
            TEST_TYPE: testInfo?.format,
            MEDIUM: testInfo?.mode,
            TAB_NAME:
              {
                "1": "Overview",
                "2": "Syllabus",
                "3": "Evaluate",
                "4": "Results",
              }[key] || key,
          });
        }}
        items={items}
        isTabsContentHeightFull={false}
      />
    </MainContainer>
  );
};

export default TestEvaluation;
